import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Save, Plus, Trash2, ExternalLink, RotateCcw, Eye, ArrowUp, ArrowDown, Globe, Star } from "lucide-react";
import MediaUploadInput from "@/components/MediaUploadInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Build a deep clone helper
const clone = (v) => JSON.parse(JSON.stringify(v ?? null));

// Get/set deep paths "hero.title.es"
function getPath(obj, path) {
  return path.split(".").reduce((a, k) => (a == null ? a : a[k]), obj);
}
function setPath(obj, path, value) {
  const parts = path.split(".");
  const next = clone(obj) || {};
  let cur = next;
  for (let i = 0; i < parts.length - 1; i++) {
    if (cur[parts[i]] == null || typeof cur[parts[i]] !== "object") cur[parts[i]] = {};
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
  return next;
}

function BilingualField({ label, path, settings, onChange, rows = 1, placeholder, helpText }) {
  const es = getPath(settings, `${path}.es`) || "";
  const en = getPath(settings, `${path}.en`) || "";
  const Comp = rows > 1 ? Textarea : Input;
  return (
    <div className="space-y-2">
      <Label className="text-slate-300 text-xs uppercase tracking-wider">{label}</Label>
      {helpText && <p className="text-xs text-slate-500">{helpText}</p>}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className="text-[10px] font-mono text-slate-500">ES</span>
          <Comp rows={rows} value={es} placeholder={placeholder} onChange={(e) => onChange(setPath(settings, `${path}.es`, e.target.value))}
                className="bg-white/5 border-white/10 text-white" />
        </div>
        <div>
          <span className="text-[10px] font-mono text-slate-500">EN</span>
          <Comp rows={rows} value={en} placeholder={placeholder} onChange={(e) => onChange(setPath(settings, `${path}.en`, e.target.value))}
                className="bg-white/5 border-white/10 text-white" />
        </div>
      </div>
    </div>
  );
}

function PlainField({ label, path, settings, onChange, type = "text", helpText }) {
  return (
    <div className="space-y-2">
      <Label className="text-slate-300 text-xs uppercase tracking-wider">{label}</Label>
      {helpText && <p className="text-xs text-slate-500">{helpText}</p>}
      <Input type={type} value={getPath(settings, path) || ""} onChange={(e) => onChange(setPath(settings, path, e.target.value))}
             className="bg-white/5 border-white/10 text-white" />
    </div>
  );
}

function ColorField({ label, path, settings, onChange }) {
  const v = getPath(settings, path) || "#000000";
  return (
    <div className="space-y-2">
      <Label className="text-slate-300 text-xs">{label}</Label>
      <div className="flex gap-2">
        <input type="color" value={v} onChange={(e) => onChange(setPath(settings, path, e.target.value))}
               className="w-10 h-9 rounded border border-white/10 bg-transparent cursor-pointer" />
        <Input value={v} onChange={(e) => onChange(setPath(settings, path, e.target.value))}
               className="bg-white/5 border-white/10 text-white flex-1 font-mono" />
      </div>
    </div>
  );
}

// Image field with drag-drop upload (uses /api/admin/upload — super admin only).
function ImageField({ label, path, settings, onChange, helpText, accept = "image/*", previewOnly = false, uploadAxios, testId }) {
  const value = getPath(settings, path) || "";
  return (
    <div className="space-y-2">
      <Label className="text-slate-300 text-xs uppercase tracking-wider">{label}</Label>
      {helpText && <p className="text-xs text-slate-500">{helpText}</p>}
      <MediaUploadInput
        value={value}
        onChange={(url) => onChange(setPath(settings, path, url))}
        accept={accept}
        uploadEndpoint="/admin/upload"
        uploadAxios={uploadAxios}
        previewOnly={previewOnly}
        testIdPrefix={testId || `img-${path.replace(/\./g, "-")}`}
      />
    </div>
  );
}

function JsonEditor({ label, path, settings, onChange, helpText }) {
  const value = getPath(settings, path);
  const [text, setText] = useState(JSON.stringify(value ?? [], null, 2));
  const [err, setErr] = useState("");
  useEffect(() => { setText(JSON.stringify(value ?? [], null, 2)); }, [JSON.stringify(value)]); // eslint-disable-line
  const apply = () => {
    try {
      const parsed = JSON.parse(text);
      onChange(setPath(settings, path, parsed));
      setErr("");
    } catch (e) {
      setErr(e.message);
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <Label className="text-slate-300 text-xs uppercase tracking-wider">{label}</Label>
        <Button size="sm" variant="outline" onClick={apply}>Aplicar JSON</Button>
      </div>
      {helpText && <p className="text-xs text-slate-500">{helpText}</p>}
      <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)}
                className="bg-black/40 border-white/10 text-emerald-300 font-mono text-xs" />
      {err && <p className="text-xs text-red-400">JSON inválido: {err}</p>}
    </div>
  );
}

export default function AdminLanding() {
  const { getAdminAxios } = useOutletContext();
  const [settings, setSettings] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saveError, setSaveError] = useState("");

  const loadAll = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        getAdminAxios().get(`${API}/admin/landing`),
        getAdminAxios().get(`${API}/admin/landing/plans`),
      ]);
      setSettings(s.data);
      setPlans(p.data);
    } finally { setLoading(false); }
  }, [getAdminAxios]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveSettings = async () => {
    setSaving(true);
    setSaveError("");
    try {
      await getAdminAxios().put(`${API}/admin/landing`, settings);
      setSavedAt(new Date());
      // Force-refresh from server so the editor reflects the persisted document
      const { data } = await getAdminAxios().get(`${API}/admin/landing`);
      setSettings(data);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message;
      if (status === 401 || status === 403) {
        setSaveError("Sesión expirada o sin permisos — cerrá sesión y volvé a entrar como super admin.");
      } else if (status === 404) {
        setSaveError("Endpoint no existe en este servidor. Falta hacer Redeploy de la última versión del backend.");
      } else {
        setSaveError(`No se pudo guardar (HTTP ${status || "?"}): ${detail}`);
      }
    } finally { setSaving(false); }
  };

  const resetAll = async () => {
    if (!window.confirm("¿Resetear toda la landing a los valores por defecto? Esto NO se puede deshacer."))
      return;
    await getAdminAxios().post(`${API}/admin/landing/reset`);
    await loadAll();
  };

  const moveSection = (key, dir) => {
    const order = settings.section_order || [];
    const i = order.indexOf(key);
    if (i < 0) return;
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j], next[i]];
    setSettings(setPath(settings, "section_order", next));
  };

  // Plans
  const refreshPlans = async () => {
    const { data } = await getAdminAxios().get(`${API}/admin/landing/plans`);
    setPlans(data);
  };

  const updatePlan = async (id, patch) => {
    const plan = plans.find((p) => p.id === id);
    await getAdminAxios().put(`${API}/admin/landing/plans/${id}`, { ...plan, ...patch });
    refreshPlans();
  };

  const deletePlan = async (id) => {
    if (!window.confirm("¿Eliminar este plan?")) return;
    await getAdminAxios().delete(`${API}/admin/landing/plans/${id}`);
    refreshPlans();
  };

  const addPlan = async () => {
    const newPlan = {
      slug: `plan-${Date.now()}`,
      name: { es: "NUEVO PLAN", en: "NEW PLAN" },
      tagline: { es: "", en: "" },
      highlighted: false,
      color: "#22ff9d",
      order: plans.length,
      billing: {
        monthly: { price: 0, currency: "USD", period_label: { es: "/mes", en: "/mo" } },
        yearly:  { price: 0, currency: "USD", period_label: { es: "/año", en: "/yr" } },
      },
      features: [],
      cta_label: { es: "Elegir", en: "Choose" },
      cta_link: "/register",
      is_active: true,
    };
    await getAdminAxios().post(`${API}/admin/landing/plans`, newPlan);
    refreshPlans();
  };

  if (loading || !settings) {
    return <div className="p-8"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;
  }

  const adminAxios = getAdminAxios();
  const enabled = settings.sections_enabled || {};
  const order = settings.section_order || [];

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-landing-page">
      <div className="flex items-start justify-between gap-3 mb-8 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2"><Globe className="w-6 h-6" /> Landing Page</h1>
          <p className="text-sm text-slate-400 mt-1">Editá cada texto, color, sección y plan que ven los visitantes en tu home.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => window.open(`/?_=${Date.now()}`, "_blank")} variant="outline" data-testid="preview-landing-btn">
            <Eye className="w-4 h-4 mr-2" /> Preview
          </Button>
          <Button onClick={resetAll} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="reset-landing-btn">
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
          <Button onClick={saveSettings} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="save-landing-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar cambios
          </Button>
        </div>
      </div>
      {savedAt && !saveError && (
        <div className="mb-4 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-300">
          ✓ Guardado a las {savedAt.toLocaleTimeString()} — abrí la landing en otra pestaña (botón Preview) o forzá refresh con <kbd className="font-mono px-1 bg-black/40 rounded">Ctrl/Cmd + Shift + R</kbd> para verla.
        </div>
      )}
      {saveError && (
        <div className="mb-4 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="save-error">
          ⚠️ {saveError}
        </div>
      )}

      <Tabs defaultValue="general">
        <TabsList className="bg-white/5 border border-white/10 flex-wrap h-auto">
          <TabsTrigger value="general" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10">General</TabsTrigger>
          <TabsTrigger value="sections" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10">Secciones</TabsTrigger>
          <TabsTrigger value="hero" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10">Hero</TabsTrigger>
          <TabsTrigger value="features" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10">Features</TabsTrigger>
          <TabsTrigger value="demo" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10">Demo</TabsTrigger>
          <TabsTrigger value="how" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10">Cómo funciona</TabsTrigger>
          <TabsTrigger value="plans" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10">Planes</TabsTrigger>
          <TabsTrigger value="testimonials" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10">Testimonios</TabsTrigger>
          <TabsTrigger value="faq" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10">FAQ</TabsTrigger>
          <TabsTrigger value="contact" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10">Contacto</TabsTrigger>
          <TabsTrigger value="footer" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10">Footer</TabsTrigger>
          <TabsTrigger value="reminders" className="text-slate-400 data-[state=active]:text-white data-[state=active]:bg-white/10">Recordatorios</TabsTrigger>
        </TabsList>

        {/* GENERAL */}
        <TabsContent value="general">
          <Card className="bg-[#13112b] border-indigo-900/30 mt-4">
            <CardHeader><CardTitle className="text-white text-base">Marca & Tema</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <PlainField label="Brand name" path="brand_name" settings={settings} onChange={setSettings} />
                <PlainField label="Idioma por defecto (es / en)" path="language_default" settings={settings} onChange={setSettings} />
              </div>
              <BilingualField label="Tagline" path="brand_tagline" settings={settings} onChange={setSettings} />
              <div className="grid grid-cols-2 gap-4">
                <ImageField label="Logo" path="logo_url" settings={settings} onChange={setSettings}
                            uploadAxios={adminAxios} previewOnly helpText="Subí o arrastra una imagen (PNG/SVG/WEBP)" testId="logo-upload" />
                <ImageField label="Favicon" path="favicon_url" settings={settings} onChange={setSettings}
                            uploadAxios={adminAxios} previewOnly accept="image/*,.ico,.png,.svg" helpText="Idealmente 32×32 o 64×64 px (PNG/SVG/.ico)" testId="favicon-upload" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider mb-3 block">Colores del tema</Label>
                <div className="grid grid-cols-3 gap-3">
                  <ColorField label="Background" path="theme.background" settings={settings} onChange={setSettings} />
                  <ColorField label="Surface" path="theme.surface" settings={settings} onChange={setSettings} />
                  <ColorField label="Border" path="theme.border" settings={settings} onChange={setSettings} />
                  <ColorField label="Primary (neón)" path="theme.primary" settings={settings} onChange={setSettings} />
                  <ColorField label="Accent" path="theme.accent" settings={settings} onChange={setSettings} />
                  <ColorField label="Text" path="theme.text" settings={settings} onChange={setSettings} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SECCIONES */}
        <TabsContent value="sections">
          <Card className="bg-[#13112b] border-indigo-900/30 mt-4">
            <CardHeader>
              <CardTitle className="text-white text-base">Secciones — orden y visibilidad</CardTitle>
              <CardDescription className="text-slate-400">Apagá las que no querés mostrar y arrastrá con las flechas para reordenar.</CardDescription>
            </CardHeader>
            <CardContent>
              {order.map((key, idx) => (
                <div key={key} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-3">
                    <code className="text-amber-400 font-mono text-sm w-32">{key}</code>
                    <span className="text-xs text-slate-500">posición {idx + 1}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="icon" variant="ghost" onClick={() => moveSection(key, -1)} className="h-7 w-7 text-slate-400"><ArrowUp className="w-3 h-3" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => moveSection(key, 1)} className="h-7 w-7 text-slate-400"><ArrowDown className="w-3 h-3" /></Button>
                    <Switch checked={enabled[key] !== false} onCheckedChange={(v) => setSettings(setPath(settings, `sections_enabled.${key}`, v))} className="data-[state=checked]:bg-amber-500" data-testid={`section-toggle-${key}`} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* HERO */}
        <TabsContent value="hero">
          <Card className="bg-[#13112b] border-indigo-900/30 mt-4">
            <CardHeader><CardTitle className="text-white text-base">Hero (parte de arriba)</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <BilingualField label="Eyebrow (texto pequeño arriba)" path="hero.eyebrow" settings={settings} onChange={setSettings} />
              <BilingualField label="Título principal" path="hero.title" settings={settings} onChange={setSettings} rows={3} helpText="Usá \\n para saltos de línea" />
              <BilingualField label="Subtítulo" path="hero.subtitle" settings={settings} onChange={setSettings} rows={3} />
              <div className="grid grid-cols-2 gap-3">
                <BilingualField label="Botón primario" path="hero.cta_primary" settings={settings} onChange={setSettings} />
                <BilingualField label="Botón secundario" path="hero.cta_secondary" settings={settings} onChange={setSettings} />
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <Label className="text-slate-300">Mostrar terminal animada</Label>
                <Switch checked={settings.hero?.show_terminal_demo !== false} onCheckedChange={(v) => setSettings(setPath(settings, "hero.show_terminal_demo", v))} />
              </div>
              <JsonEditor label="Líneas de la terminal (array de strings)" path="hero.terminal_lines" settings={settings} onChange={setSettings}
                          helpText='Ej: ["$ comando", "✓ ok", "→ listo"]' />
            </CardContent>
          </Card>
        </TabsContent>

        {/* FEATURES */}
        <TabsContent value="features">
          <Card className="bg-[#13112b] border-indigo-900/30 mt-4">
            <CardHeader><CardTitle className="text-white text-base">Features</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <BilingualField label="Eyebrow" path="features.eyebrow" settings={settings} onChange={setSettings} />
              <BilingualField label="Título" path="features.title" settings={settings} onChange={setSettings} />
              <BilingualField label="Subtítulo" path="features.subtitle" settings={settings} onChange={setSettings} rows={2} />
              <JsonEditor label="Items (array de features)" path="features.items" settings={settings} onChange={setSettings}
                          helpText="Cada item: {icon, title:{es,en}, desc:{es,en}}. Iconos disponibles: Bot, Zap, Sparkles, Package, Users, Shield" />
            </CardContent>
          </Card>
        </TabsContent>

        {/* DEMO */}
        <TabsContent value="demo">
          <Card className="bg-[#13112b] border-indigo-900/30 mt-4">
            <CardHeader><CardTitle className="text-white text-base">Demo (video o imagen)</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <BilingualField label="Eyebrow" path="demo.eyebrow" settings={settings} onChange={setSettings} />
              <BilingualField label="Título" path="demo.title" settings={settings} onChange={setSettings} />
              <BilingualField label="Subtítulo" path="demo.subtitle" settings={settings} onChange={setSettings} rows={2} />
              <PlainField label="URL del video (YouTube embed o mp4)" path="demo.video_url" settings={settings} onChange={setSettings}
                          helpText="Ej: https://www.youtube.com/embed/ABC123 — o subí un archivo abajo" />
              <ImageField label="Subir video propio (mp4)" path="demo.video_url" settings={settings} onChange={setSettings}
                          uploadAxios={adminAxios} accept="video/*" previewOnly testId="demo-video-upload"
                          helpText="Reemplaza la URL de arriba si subes uno" />
              <ImageField label="Imagen alternativa (si no hay video)" path="demo.image_url" settings={settings} onChange={setSettings}
                          uploadAxios={adminAxios} previewOnly testId="demo-image-upload" />
            </CardContent>
          </Card>
        </TabsContent>

        {/* HOW IT WORKS */}
        <TabsContent value="how">
          <Card className="bg-[#13112b] border-indigo-900/30 mt-4">
            <CardHeader><CardTitle className="text-white text-base">Cómo funciona</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <BilingualField label="Eyebrow" path="how_it_works.eyebrow" settings={settings} onChange={setSettings} />
              <BilingualField label="Título" path="how_it_works.title" settings={settings} onChange={setSettings} />
              <JsonEditor label="Pasos" path="how_it_works.steps" settings={settings} onChange={setSettings}
                          helpText='Cada paso: {number:"01", title:{es,en}, desc:{es,en}}' />
            </CardContent>
          </Card>
        </TabsContent>

        {/* PLANS */}
        <TabsContent value="plans">
          <Card className="bg-[#13112b] border-indigo-900/30 mt-4">
            <CardHeader>
              <CardTitle className="text-white text-base">Sección Planes — encabezado</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <BilingualField label="Eyebrow" path="plans_section.eyebrow" settings={settings} onChange={setSettings} />
              <BilingualField label="Título" path="plans_section.title" settings={settings} onChange={setSettings} />
              <BilingualField label="Subtítulo" path="plans_section.subtitle" settings={settings} onChange={setSettings} rows={2} />
              <BilingualField label="Texto del descuento anual" path="plans_section.yearly_discount_label" settings={settings} onChange={setSettings} />
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <Label className="text-slate-300">Mostrar toggle Mensual / Anual</Label>
                <Switch checked={settings.plans_section?.show_billing_toggle !== false} onCheckedChange={(v) => setSettings(setPath(settings, "plans_section.show_billing_toggle", v))} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#13112b] border-indigo-900/30 mt-4">
            <CardHeader className="flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white text-base">Planes</CardTitle>
                <CardDescription className="text-slate-400">Tu cliente verá estos en la landing.</CardDescription>
              </div>
              <Button onClick={addPlan} className="bg-amber-500 hover:bg-amber-600 text-black"><Plus className="w-4 h-4 mr-1" /> Agregar plan</Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {plans.map((p) => <PlanRow key={p.id} plan={p} onUpdate={(patch) => updatePlan(p.id, patch)} onDelete={() => deletePlan(p.id)} />)}
              {plans.length === 0 && <p className="text-sm text-slate-500">Sin planes. Hacé click en "Agregar plan".</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TESTIMONIALS */}
        <TabsContent value="testimonials">
          <Card className="bg-[#13112b] border-indigo-900/30 mt-4">
            <CardHeader><CardTitle className="text-white text-base">Testimonios</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <BilingualField label="Eyebrow" path="testimonials.eyebrow" settings={settings} onChange={setSettings} />
              <BilingualField label="Título" path="testimonials.title" settings={settings} onChange={setSettings} />
              <JsonEditor label="Items" path="testimonials.items" settings={settings} onChange={setSettings}
                          helpText='[{name, role:{es,en}, avatar_url, quote:{es,en}}]' />
            </CardContent>
          </Card>
        </TabsContent>

        {/* FAQ */}
        <TabsContent value="faq">
          <Card className="bg-[#13112b] border-indigo-900/30 mt-4">
            <CardHeader><CardTitle className="text-white text-base">FAQ</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <BilingualField label="Eyebrow" path="faq.eyebrow" settings={settings} onChange={setSettings} />
              <BilingualField label="Título" path="faq.title" settings={settings} onChange={setSettings} />
              <JsonEditor label="Preguntas y respuestas" path="faq.items" settings={settings} onChange={setSettings}
                          helpText='[{q:{es,en}, a:{es,en}}]' />
            </CardContent>
          </Card>
        </TabsContent>

        {/* CONTACT */}
        <TabsContent value="contact">
          <Card className="bg-[#13112b] border-indigo-900/30 mt-4">
            <CardHeader><CardTitle className="text-white text-base">Contacto (botón WhatsApp)</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <BilingualField label="Eyebrow" path="contact.eyebrow" settings={settings} onChange={setSettings} />
              <BilingualField label="Título" path="contact.title" settings={settings} onChange={setSettings} />
              <BilingualField label="Subtítulo" path="contact.subtitle" settings={settings} onChange={setSettings} rows={2} />
              <PlainField label="Tu número de WhatsApp (formato internacional sin +, ej: 5491100000000)" path="contact.whatsapp_phone" settings={settings} onChange={setSettings} />
              <BilingualField label="Mensaje pre-cargado al abrir WhatsApp" path="contact.whatsapp_message" settings={settings} onChange={setSettings} rows={2} />
              <BilingualField label="Texto del botón" path="contact.cta_label" settings={settings} onChange={setSettings} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* FOOTER */}
        <TabsContent value="footer">
          <Card className="bg-[#13112b] border-indigo-900/30 mt-4">
            <CardHeader><CardTitle className="text-white text-base">Footer</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <BilingualField label="Tagline" path="footer.tagline" settings={settings} onChange={setSettings} rows={2} />
              <BilingualField label="Copyright" path="footer.copyright" settings={settings} onChange={setSettings} />
              <div className="grid grid-cols-2 gap-3">
                <PlainField label="Instagram URL" path="footer.social.instagram" settings={settings} onChange={setSettings} />
                <PlainField label="Twitter/X URL" path="footer.social.twitter" settings={settings} onChange={setSettings} />
                <PlainField label="YouTube URL" path="footer.social.youtube" settings={settings} onChange={setSettings} />
                <PlainField label="GitHub URL" path="footer.social.github" settings={settings} onChange={setSettings} />
              </div>
              <JsonEditor label="Links legales" path="footer.legal_links" settings={settings} onChange={setSettings}
                          helpText='[{label:{es,en}, url}]' />
            </CardContent>
          </Card>
        </TabsContent>

        {/* REMINDERS (Phase 2 placeholder) */}
        <TabsContent value="reminders">
          <Card className="bg-[#13112b] border-indigo-900/30 mt-4">
            <CardHeader>
              <CardTitle className="text-white text-base">Recordatorios de vencimiento (Beta)</CardTitle>
              <CardDescription className="text-slate-400">Configurá los avisos por WhatsApp que se mandan a tus clientes cuando se les vence el plan.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-slate-300">Activar recordatorios</Label>
                  <p className="text-xs text-slate-500">Cuando lo enciendas, el bot enviará los avisos según los días configurados.</p>
                </div>
                <Switch checked={!!settings.reminders?.enabled} onCheckedChange={(v) => setSettings(setPath(settings, "reminders.enabled", v))} />
              </div>
              <PlainField label="Avisar X días ANTES del vencimiento (separado por comas)" path="reminders.remind_days_before" settings={settings} onChange={setSettings}
                          helpText="Ej: 7,1 = avisará 7 días antes y 1 día antes. Editá el JSON tal cual." />
              <PlainField label="Avisar X días DESPUÉS del vencimiento (separado por comas)" path="reminders.remind_after_expiry_days" settings={settings} onChange={setSettings} />
              <BilingualField label="Plantilla del mensaje" path="reminders.message_template" settings={settings} onChange={setSettings} rows={4}
                              helpText="Variables: {{name}}, {{plan}}, {{expires_on}}, {{renew_url}}" />
              <PlainField label="URL de renovación" path="reminders.renew_url" settings={settings} onChange={setSettings} />
              <p className="text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded p-3">
                ✅ El cron de WhatsApp está activo: cada 2 hs revisa qué clientes están a 3 días de vencer, en el día del vencimiento o un día después, y les manda el aviso. Acordate que el bot de cada cliente debe estar conectado para que se envíen.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---- Plan editor row ----
function PlanRow({ plan, onUpdate, onDelete }) {
  const [local, setLocal] = useState(plan);
  useEffect(() => { setLocal(plan); }, [plan]);
  const setLocalPath = (path, value) => setLocal(setPath(local, path, value));
  const save = () => onUpdate(local);
  return (
    <div className="border border-white/10 rounded-lg p-4 space-y-3 bg-black/20" data-testid={`plan-edit-${plan.slug}`}>
      <div className="flex items-center gap-2 justify-between flex-wrap">
        <div className="flex items-center gap-3">
          <input type="color" value={local.color || "#22ff9d"} onChange={(e) => setLocalPath("color", e.target.value)}
                 className="w-8 h-8 rounded border border-white/10 bg-transparent" />
          <code className="font-mono text-sm" style={{ color: local.color }}>{local.slug}</code>
          {local.highlighted && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 flex items-center gap-1"><Star className="w-3 h-3" /> destacado</span>}
        </div>
        <div className="flex gap-2 items-center">
          <Label className="text-slate-300 text-xs">Activo</Label>
          <Switch checked={local.is_active !== false} onCheckedChange={(v) => setLocalPath("is_active", v)} />
          <Button size="sm" onClick={save} className="bg-amber-500 text-black hover:bg-amber-600"><Save className="w-3 h-3 mr-1" /> Guardar</Button>
          <Button size="sm" variant="outline" onClick={onDelete} className="border-red-500/30 text-red-400"><Trash2 className="w-3 h-3" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <PlainField label="Slug (interno)" path="slug" settings={local} onChange={setLocal} />
        <PlainField label="Color" path="color" settings={local} onChange={setLocal} />
      </div>
      <BilingualField label="Nombre" path="name" settings={local} onChange={setLocal} />
      <BilingualField label="Tagline" path="tagline" settings={local} onChange={setLocal} />
      <div className="grid grid-cols-2 gap-3">
        <BilingualField label="Texto botón CTA" path="cta_label" settings={local} onChange={setLocal} />
        <PlainField label="Link CTA" path="cta_link" settings={local} onChange={setLocal} />
      </div>
      <div className="flex items-center justify-between p-2 bg-white/5 rounded">
        <Label className="text-slate-300 text-xs">Resaltar como "Más popular"</Label>
        <Switch checked={!!local.highlighted} onCheckedChange={(v) => setLocalPath("highlighted", v)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="border border-white/10 rounded p-3">
          <p className="text-xs uppercase text-slate-400 mb-2 font-mono">Mensual</p>
          <div className="grid grid-cols-3 gap-2">
            <PlainField label="Precio" type="number" path="billing.monthly.price" settings={local} onChange={setLocal} />
            <PlainField label="Moneda" path="billing.monthly.currency" settings={local} onChange={setLocal} />
            <BilingualField label="Sufijo" path="billing.monthly.period_label" settings={local} onChange={setLocal} />
          </div>
        </div>
        <div className="border border-white/10 rounded p-3">
          <p className="text-xs uppercase text-slate-400 mb-2 font-mono">Anual</p>
          <div className="grid grid-cols-3 gap-2">
            <PlainField label="Precio" type="number" path="billing.yearly.price" settings={local} onChange={setLocal} />
            <PlainField label="Moneda" path="billing.yearly.currency" settings={local} onChange={setLocal} />
            <BilingualField label="Sufijo" path="billing.yearly.period_label" settings={local} onChange={setLocal} />
          </div>
        </div>
      </div>
      <JsonEditor label="Features (lista de checkmarks)" path="features" settings={local} onChange={setLocal}
                  helpText='[{label:{es,en}, included:true}]' />
    </div>
  );
}
